/**
 * Returns the localized month name for a given date.
 * @param {Date|string|number} date - The date object or a value convertible to Date.
 * @param {Object} options - Options for formatting.
 * @param {string} [options.locale='en-US'] - The locale string.
 * @param {'short'|'long'} [options.length='long'] - The length of the month name.
 * @returns {string} Localized month name.
 */
export function getMonthName(date, { locale = 'en-US', length = 'long' } = {}) {
    const d = new Date(date);
    return d.toLocaleString(locale, { month: length });
}

/**
 * Returns all localized month names for a given locale.
 * @param {Object} options - Options for formatting.
 * @param {string} [options.locale='en-US'] - The locale string.
 */
export function getAllMonthNames({ locale = 'en-US' } = {}) {
    const monthNames = [];
    for (let i = 0; i < 12; i++) {
        const date = new Date(2023, i, 1);
        monthNames.push(getMonthName(date, { locale, length: 'long' }));
    }
    return monthNames;
}

/**
 * Returns the localized day name for a given date.
 * @param {Date|string|number} date - The date object or a value convertible to Date.
 * @param {Object} options - Options for formatting.
 * @param {string} [options.locale='en-US'] - The locale string.
 * @param {'short'|'long'} [options.length='long'] - The length of the day name.
 * @returns {string} Localized day name.
 */
export function getDayName(date, { locale = 'en-US', length = 'long' } = {}) {
    const d = new Date(date);
    return d.toLocaleString(locale, { weekday: length });
}

/**
 * Returns all localized day names for a given locale.
 * @param {Object} options - Options for formatting.
 * @param {string} [options.locale='en-US'] - The locale string.
 */
export function getAllDayNames({ locale = 'en-US' } = {}) {
    const dayNames = [];
    for (let i = 0; i < 7; i++) {
        const date = new Date(2023, 0, i + 1); // January 1, 2023 is a Sunday
        dayNames.push({
            long: getDayName(date, { locale, length: 'long' }),
            short: getDayName(date, { locale, length: 'short' }),
        });
    }
    return dayNames;
}

/**
 * Mapping of language codes to locale strings.
 */
export const languageToLocaleMap = {
    en: { region: 'US' },
    sv: { region: 'SE' },
};

/**
 * Returns the current locale based on the document's language attribute.
 */
export const getCurrentLocale = () => {
    const lang = document.documentElement.lang || 'en';
    const locale = new Intl.Locale(lang, languageToLocaleMap[lang]);
    return locale;
};
